import Button, { buttonClassNames } from '@components/primitives/Button'
import { Icon } from '@components/primitives/Icon'
import { Text } from '@components/primitives/Text'
import { classMerge } from '@components/utilities/classMerge'
import { faLocationDot } from '@fortawesome/pro-regular-svg-icons/faLocationDot'
import { faMicroscope } from '@fortawesome/pro-regular-svg-icons/faMicroscope'
import { faPollPeople } from '@fortawesome/pro-regular-svg-icons/faPollPeople'
import { faPrescriptionBottleMedical } from '@fortawesome/pro-regular-svg-icons/faPrescriptionBottleMedical'
import { faSparkles } from '@fortawesome/pro-regular-svg-icons/faSparkles'
import { faXmark } from '@fortawesome/pro-regular-svg-icons/faXmark'
import { transformAgeRangeFromAlgolia } from '@modules/trials/search/helpers/transformAgeRangeFromAlgolia'
import some from 'lodash/some'

import { CallToAction } from '../CallToAction.client'
import { DetailPill } from '../DetailPill'
import { HighlightTag } from '../HighlightTag'
import { TrialTitleWithConditionHighlight } from '../TrialTitleWithConditionHighlight'
import type { SearchTrialCardProps } from '../types'

export default function PreviewTrialCard({
  biologicalSex,
  briefSummary,
  briefTitle,
  condensedPhaseList,
  conjuredTitle,
  enrollmentCount,
  hasBackgroundTherapyAllowed,
  hasNoPlacebo,
  isBreakthroughTrial,
  locationCities,
  locationStates,
  maximumAge,
  minimumAge,
  nlpBriefSummary,
  onClose,
  phaseList,
  slug,
}: SearchTrialCardProps) {
  const preferredTitle = conjuredTitle ?? briefTitle ?? ''
  const preferredDescription = nlpBriefSummary ?? briefSummary ?? ''

  const phaseListLabel = condensedPhaseList

  const displayLocation = [locationCities?.[0], locationStates?.[0]]
    .filter(Boolean)
    .join(', ')

  const ageRange = transformAgeRangeFromAlgolia(minimumAge, maximumAge)

  const isAdjuvantTrial = hasBackgroundTherapyAllowed

  const isPivotalTrial = phaseList?.includes('Phase 3')

  const hasHighlights = some([
    isAdjuvantTrial,
    isBreakthroughTrial,
    isPivotalTrial,
    hasNoPlacebo,
  ])

  return (
    <div
      className={classMerge(
        'relative flex min-h-[338px] w-[343px] flex-col justify-between overflow-clip rounded-lg border border-neutral200 bg-white p-4 shadow-sm',
      )}
    >
      {onClose && (
        <div className='absolute right-0 top-0'>
          <Button.Icon
            className={classMerge(
              buttonClassNames.legacy.fill.white,
              'h-8 w-full rounded-full rounded-tr-lg !p-3 hover:!bg-none',
            )}
            icon={faXmark}
            iconClassName='text-primary600'
            iconPosition='left'
            iconSize='xs'
            onClick={onClose}
          />
        </div>
      )}
      <div>
        <TrialTitleWithConditionHighlight
          slug={slug}
          trialTitle={preferredTitle}
        />
        <div className='mt-2 flex items-center gap-1'>
          <Icon className='text-primary600' icon={faLocationDot} />
          <span className='whitespace-nowrap text-xs font-semibold leading-normal text-black antialiased'>
            {displayLocation}
          </span>
        </div>
        <span
          className='my-3 line-clamp-4 text-xs text-neutral700 antialiased'
          title={preferredDescription}
        >
          {preferredDescription}
        </span>
        {hasHighlights && (
          <div className='mt-1 grid grid-cols-2 gap-y-0.5'>
            {isAdjuvantTrial && (
              <HighlightTag
                icon={faPrescriptionBottleMedical}
                value='Stay on current meds'
              />
            )}
            {hasNoPlacebo && (
              <HighlightTag icon={faPollPeople} value='No Placebo Group' />
            )}
            {isBreakthroughTrial && (
              <HighlightTag icon={faSparkles} value='Breakthrough Therapy' />
            )}
            {isPivotalTrial && (
              <HighlightTag icon={faMicroscope} value='Pivotal Trial' />
            )}
          </div>
        )}
        <div className='mt-4' id='details'>
          <Text className='text-neutral500' styleName='p-xxtiny-strong'>
            Trial Details
          </Text>
          <div className='mt-2 flex space-x-1.5'>
            <DetailPill value={`Age: ${ageRange}`} />
            <DetailPill value={`Sex: ${biologicalSex}`} />
            <DetailPill value={phaseListLabel} />
          </div>
        </div>
      </div>
      <div className='mt-5 justify-center' id='footer'>
        <CallToAction enrollmentCount={enrollmentCount} slug={slug} />
      </div>
    </div>
  )
}
